import api, { getAuthHeaders } from '../api/api';

const handlePdfDownload = async (url: string, filename: string) => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download report');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Download error:', error);
    alert('Failed to download report. Please try again.');
  }
};

export const downloadStudentReport = async (examId: number, studentId: number) => {
  const url = `${api.defaults.baseURL}/reports/exam/${examId}/student/${studentId}`;
  handlePdfDownload(url, `report_exam_${examId}_student_${studentId}.pdf`);
};

export const downloadNominalRoll = async (examId: number, classLevel: string) => {
  const url = `${api.defaults.baseURL}/reports/exam/${examId}/nominal-roll/${classLevel}`;
  handlePdfDownload(url, `nominal_roll_exam_${examId}_${classLevel}.pdf`);
};

export const bulkGenerateReports = async (examId: number, classLevel: string) => {
  const url = `${api.defaults.baseURL}/reports/exam/${examId}/bulk-generate-reports/${classLevel}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to start bulk generation.' }));
        throw new Error(errorData.detail || 'Failed to start bulk generation.');
    }

    return await response.json();
  } catch (error) {
    console.error('Bulk generation error:', error);
    throw error;
  }
};

export const bulkGenerateCombinedPDF = async (examId: number, classLevel: string) => {
  const url = `${api.defaults.baseURL}/reports/exam/${examId}/bulk-generate-combined-pdf/${classLevel}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to start combined PDF generation.' }));
        throw new Error(errorData.detail || 'Failed to start combined PDF generation.');
    }

    return await response.json();
  } catch (error) {
    console.error('Combined PDF generation error:', error);
    throw error;
  }
};

export const checkBulkReportStatus = async (taskId: string) => {
  const url = `${api.defaults.baseURL}/reports/bulk-report-status/${taskId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to check status.' }));
    throw new Error(errorData.detail || 'Failed to check status.');
  }

  return await response.json();
};

export const getExamTrendAnalysis = async (currentExamId: number, previousExamId: number) => {
  const url = `${api.defaults.baseURL}/reports/exam-trend-analysis?current_exam_id=${currentExamId}&previous_exam_id=${previousExamId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch trend analysis.' }));
    throw new Error(errorData.detail || 'Failed to fetch trend analysis.');
  }

  return await response.json();
};

export const generateTrendAnalysisPDF = async (currentExamId: number, previousExamId: number) => {
  const url = `${api.defaults.baseURL}/reports/generate-trend-analysis-pdf`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        current_exam_id: currentExamId,
        previous_exam_id: previousExamId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to generate PDF.' }));
      throw new Error(errorData.detail || 'Failed to generate PDF.');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `exam_trend_analysis_${Date.now()}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('PDF generation error:', error);
    throw error;
  }
}; 